import React from 'react';
import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl';
import styled from 'styled-components';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import Icon from '../components/Icon';
import Label from '../components/Label';
import ICONS from '../constants/icons';
import { COLORS, MQS } from '../constants/styles';
import { useDispatch, useSelector } from 'react-redux';
import { setVerifyingEmail } from '../../../details/accountSecurity/verifyEmail/verifyEmailFormActions';
import { IReduxRootState } from '../../reduxRootState.interface';
import { UnionCardIcon } from '../components/icons/UnionCardIcon';
import { getEmailFAQUrl } from '../../../details/accountSecurity/verifyEmail/verifyEmailUtils';

const Base = styled.div`
    position: relative;
    cursor: pointer;

    &:not(:last-child) {
        margin-bottom: 40px;
    }
    @media (max-width: 436px) {
        .phoneLabel {
            display: flex;
            flex: 1 1 content;
            flex-wrap: wrap;
            word-break: keep-all;
            white-space: nowrap;
            top: -40px;
        }
        #FAQlink {
            margin-top: -3px;
            margin-left: 0px;
            flex-basis: calc(100%);
        }
    }
`;

const Content = styled.div`
    box-sizing: border-box;
    border: 1px solid ${COLORS.dark2};
    border-radius: 0.32rem;
    display: flex;
    justify-content: space-between;
    padding: 0.625rem;
`;

const Value = styled.p`
    margin: 0;
    max-width: calc(100% - 55px);
    -ms-overflow-style: none;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const EditButton = styled.button`
    border: none;
    color: ${COLORS.linkBlue};
    background-color: transparent;
    cursor: pointer;
    display: flex;
    flex-direction: row;
    gap: 6px;
    white-space: nowrap;

    @media (max-width: ${MQS.mq2Min}) {
        padding: 1px 6px;
    }
`;

const Edit = styled.div`
    display: flex;
    align-items: center;
    & span {
        color: ${COLORS.linkBlue};
        font-size: 0.875rem;
    }
`;
const LabelWrapper = styled.p`
    margin: 0;
`;

const IconWrapper = styled.span`
    display: inline-block;
    margin-left: 0.2em;
`;
interface IEditFieldProps {
    value: string;
    label: FormattedMessage.MessageDescriptor | string;
    id: string;
    onEdit: () => any;
    qaId?: string;
    emailVerified?: boolean;
    onVerify?: () => void;
}
const Link = styled.a`
    color: ${COLORS.linkBlue};
    cursor: pointer;
    transition: font-weight 100ms ease-in;
    text-decoration: underline;
    font-size: 0.75rem;
    &:hover {
        font-weight: bold;
    }
    margin-top: 5px;
    pointer-events: auto !important;
`;

const VerifyLabel = styled.span<{
    emailVerified?: boolean;
}>`
    display: inline-block;
    margin-left: 8px;
    color: ${({ emailVerified }) => (emailVerified ? COLORS.green : COLORS.error)};
`;
const HintText = styled.div`
    padding-bottom: 0.5rem;
    padding-left: 0.6rem;
    font-size: 0.75rem;
    color: ${COLORS.grey7};
`;

const EditField = ({
    value,
    label,
    onEdit,
    id,
    qaId,
    emailVerified,
    onVerify,
    intl,
}: IEditFieldProps & InjectedIntlProps) => {
    const dispatch = useDispatch();
    const {
        member,
        app: {
            locale,
            clientConfig: { emailVerificationFAQUrls, internalIdentifier },
        },
    } = useSelector((state: IReduxRootState) => state);
    const isEmailVerifiedNull = member['emailVerified'] === null;
    const isAMClient = internalIdentifier === 'am';
    const isRFBClient = internalIdentifier === 'rfb';
    const isJPClient = internalIdentifier === 'jp';
    const emailFAQUrl = getEmailFAQUrl(emailVerificationFAQUrls, locale);
    const onClickVerifyLink = (
        event:
            | React.MouseEvent<HTMLAnchorElement, MouseEvent>
            | React.KeyboardEvent<HTMLAnchorElement>,
    ) => {
        event.preventDefault();
        event.stopPropagation();
        dispatch(setVerifyingEmail(member.email as string));
        onVerify && onVerify();
    };
    const showHintText = !emailVerified;
    const isEmailField = qaId === 'email';

    return (
        <Base id={id} onClick={onEdit}>
            <LabelWrapper>
                <Label
                    labelMessage={label}
                    fixed={Boolean(value)}
                    htmlFor={id}
                    id={`${id}Label`}
                    className={isEmailField ? 'phoneLabel' : ''}
                >
                    {isEmailField && !isEmailVerifiedNull && (
                        <VerifyLabel emailVerified={emailVerified}>
                            {TranslationHelpers.getFormattedMessage(
                                emailVerified ? 'email.verified' : 'email.not.verified',
                            )}
                        </VerifyLabel>
                    )}
                    {isEmailField && !isRFBClient && !isAMClient && (
                        <VerifyLabel emailVerified={emailVerified} id="FAQlink">
                            <Link
                                href={`${emailFAQUrl ?? '/'}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                                target="_blank"
                            >
                                {TranslationHelpers.getFormattedMessage('email.verify.about')}
                                <IconWrapper>
                                    <UnionCardIcon />
                                </IconWrapper>
                            </Link>
                        </VerifyLabel>
                    )}
                </Label>
                {isEmailField && showHintText && isJPClient && (
                    <HintText>
                        {TranslationHelpers.getFormattedMessage('email.verify.hintText')}
                    </HintText>
                )}
            </LabelWrapper>
            <Content>
                <Value>{value}</Value>
                {
                    <Edit className="editEditField" data-qa-id={`${qaId}-edit-field`}>
                        <EditButton
                            data-qa-id={`${qaId}-button`}
                            aria-labelledby={`${id}Label`}
                            aria-label={intl.formatMessage(
                                TranslationHelpers.getMessageDescriptor('button.edit'),
                            )}
                        >
                            <Icon icon={ICONS.EDIT} size="16" />
                            <span>{TranslationHelpers.getFormattedMessage('button.edit')}</span>
                        </EditButton>
                    </Edit>
                }
            </Content>
            {isEmailField && !isEmailVerifiedNull && !emailVerified && (
                <Link
                    onClick={(e) => onClickVerifyLink(e)}
                    style={{ position: 'absolute', marginLeft: '0.6rem' }}
                    tabIndex={0}
                    onKeyUp={(e) => {
                        if (e.keyCode === 13 || e.keyCode === 32) onClickVerifyLink(e);
                    }}
                >
                    {TranslationHelpers.getFormattedMessage('email.verify.link')}
                </Link>
            )}
        </Base>
    );
};

export default injectIntl(EditField);
